import { createContext } from "react";
import { ModuleInfo, ModuleManifest, ComponentSubscription } from "./model";
import { ChannelSubscriptionProvider } from "./api";

/** The analytics identifier setter function. */
export interface AnalyticsSetter {

  /**
   * Sets the amplitude analytic id.
   * @param id The ID.
   */
  (id: string): void;
}

/** The dynamic subscription deregister function. */
export interface DynamicSubscriptionDeregistrationFunc {

  /** Deregisters a dynamic subscription. */
  (): void;
}

/** The dynamic subscription registration function. */
export interface DynamicSubscriptionRegistrationFunc {

  /**
   * Registers a dynamic subcription field for the specified module and channel.
   * @param moduleId The ID of the module containing the channel.
   * @param channelId The channel ID.
   * @param subscription The subscription being registered.
   */
  (moduleId: string | null, channelId: string, subscription: ComponentSubscription): DynamicSubscriptionDeregistrationFunc;
}

/** The shell context model. */
export interface ShellContextModel {

  /** Gets the shell root path (usually "/"). */
  readonly rootPath: string;

  /** Gets the shell module root path (usually "/"). */
  readonly moduleRootPath: string;

  /** The module the shell will redirect to when the root path is used. */
  readonly rootModule: ModuleInfo | null;

  /** The set of available modules. */
  readonly modules: ReadonlyArray<ModuleManifest>;

  /** Increments the loading count. */
  readonly incrementLoadingCount: () => void;

  /** Decrements the loading count. */
  readonly decrementLoadingCount: () => void;

  /** Gets the channel subscriptions. */
  readonly getChannelSubscriptions: ChannelSubscriptionProvider;

  /** Registers a dynamic content channel subscription. */
  readonly registerDynamicChannelSubscription: DynamicSubscriptionRegistrationFunc;

  /** Sets the amplitude analytics id. */
  readonly setAnalyticsId: AnalyticsSetter;

  /** Indicates whether a logout is currently in progress. Used to prevent race conditions during auth state transitions. */
  readonly isLoggingOut: boolean;

  /** Notifies the shell that a logout has started. Call this before clearing auth state to prevent module reload race conditions. */
  readonly notifyLogoutStarted: () => void;
}

/** The host module context. */
export const HostModuleContext = createContext<ModuleInfo | null>(null);

/** The owner module context. */
export const OwnerModuleContext = createContext<ModuleInfo | null>(null);

/** A no-operation function. */
function noOp(): void {
  /** No operation */
}

/**
 * An identity function that returns a function returning the passed in value.
 * @param val The value to return.
 * @returns An identity function returning the passed in value.
 */
function identity<T>(val: T): () => T {
  return (): T => val;
}

/** The shell context. */
export const ShellContext = createContext<ShellContextModel>({
  rootPath: "/",
  moduleRootPath: "/",
  rootModule: null,
  modules: [],
  incrementLoadingCount: noOp,
  decrementLoadingCount: noOp,
  getChannelSubscriptions: identity([]),
  registerDynamicChannelSubscription: identity(noOp),
  setAnalyticsId: noOp,
  isLoggingOut: false,
  notifyLogoutStarted: noOp,
});
